import {
  Alert,
  Box,
  Button,
  Card,
  createStyles,
  Drawer,
  Group,
  List,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Title,
  UnstyledButton,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useRouter } from 'next/router'
import React, { useState } from 'react'

import { DateField } from '@/components/commons/DisplayData'
import { useFetch } from '@/lib/hooks'
import { getUrlString } from '@/lib/utils'
import { Seller as SellerType } from '@/types'
import { Policy as PolicyType } from '@/types/policy'

const useStyles = createStyles(theme => ({
  title: {
    backgroundColor: 'white',
    position: 'sticky',
    top: 0
  },
  item: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    '&:last-child': { borderBottom: 'none' }
  },
  itemWrapper: { width: '100%' }
}))

interface Props {
  seller?: SellerType
  sellerSlug?: string
  variant?: 'alert' | 'card' | 'inline'
}

export default function Policies({ seller, sellerSlug, variant = 'card' }: Props) {
  // Styles
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)

  // Hooks
  const router = useRouter()
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)
  const finalSellerSlug = seller?.slug || sellerSlug

  // States
  const [open, setOpen] = useState<boolean>(false)

  // Fetch
  const { data: policies, isLoading } = useFetch([
    siteSlug && finalSellerSlug ? `/${siteSlug}/policies/${finalSellerSlug}/` : null
  ])

  // Actions
  const getPoliciesTitle = (policies: any[]) => {
    if (!Array.isArray(policies) || !policies.length) return ''

    const tagMap = {
      checkout: 'Compra',
      exchange: 'Troca',
      return: 'Devolução',
      cancellation: 'Cancelamento'
    }
    const order = ['checkout', 'exchange', 'return', 'cancellation']

    const availableTags = new Set(policies.flatMap(policy => policy?.tags || []))
    const availableTitles = order
      .filter(tag => availableTags.has(tag))
      .map(tag => tagMap[tag as keyof typeof tagMap])

    if (!availableTitles.length) return ''
    if (availableTitles.length === 1) return availableTitles[0]
    if (availableTitles.length === 2) return `${availableTitles[0]} e ${availableTitles[1]}`

    const lastItem = availableTitles.pop()
    return `${availableTitles.join(', ')} e ${lastItem}`
  }

  const getValidPoliciesCount = (policies: any[]) =>
    Array.isArray(policies)
      ? policies.filter(policy =>
        policy?.tags?.some((tag: string) =>
          ['checkout', 'exchange', 'return', 'cancellation'].includes(tag)
        )
      ).length
      : 0

  // Constants
  const policiesList = getPoliciesTitle(policies)
  const validPoliciesCount = getValidPoliciesCount(policies)
  const policiesWithContent =
    policies?.filter((policy: any) => policy?.content?.html && policy.content.html.trim() !== '') ||
    []

  // Renders
  const renderSkeleton = () => {
    switch (variant) {
      case 'alert':
        return <Skeleton height={120} radius="md" />
      case 'card':
        return (
          <Card.Section sx={{ borderTop: '1px solid #e9ecef' }}>
            <Group position="apart" p="md">
              <Skeleton height={20} width="60%" />
              <Skeleton height={32} width="120px" radius="sm" />
            </Group>
          </Card.Section>
        )
      case 'inline':
        return (
          <Group position="apart" p="md" sx={{ borderBottom: `1px solid ${theme.colors.gray[3]}` }}>
            <Skeleton height={20} width="50%" />
            <Skeleton height={28} width="100px" radius="sm" />
          </Group>
        )
      default:
        return <Skeleton height={80} radius="md" />
    }
  }
  const renderDrawerSkeleton = () => (
    <Stack spacing="md">
      {[1, 2, 3].map(i => (
        <Box key={i}>
          <Skeleton height={24} width="70%" mb="xs" />
          <Skeleton height={12} width="40%" mb="sm" />
          <Skeleton height={16} width="100%" mb="xs" />
          <Skeleton height={16} width="95%" mb="xs" />
          <Skeleton height={16} width="85%" mb="md" />
        </Box>
      ))}
    </Stack>
  )

  const renderContent = () => {
    switch (variant) {
      case 'alert':
        return (
          <UnstyledButton onClick={() => setOpen(true)}>
            <Alert
              title={`Políticas
                          ${validPoliciesCount > 0 ? 'de' : ''} ${policiesList}`}>
              <Text mb="sm">
                <strong>{seller?.displayName}</strong> realiza suas vendas em nossa plataforma
                respeitando regras e condições definidas em nossas políticas de{' '}
                <strong>{policiesList}</strong> que se aplicam tanto à venda de produtos quanto à de serviços. Gostaria de consultar os detalhes antes de prosseguir?
              </Text>
              <Group position="right">
                <Button onClick={() => setOpen(true)}>Ver políticas</Button>
              </Group>
            </Alert>
          </UnstyledButton>
        )

      case 'card':
        return (
          <Card.Section sx={{ borderTop: '1px solid #e9ecef' }}>
            <List
              listStyleType="none"
              classNames={{ item: classes.item, itemWrapper: classes.itemWrapper }}>
              <List.Item>
                <Group position="apart" p="md">
                  <Text>
                    <strong>
                      Políticas {validPoliciesCount > 0 ? 'de' : ''} {policiesList}
                    </strong>
                  </Text>
                  <Text>
                    <Button variant="outline" onClick={() => setOpen(true)}>
                      Ver políticas
                    </Button>
                  </Text>
                </Group>
              </List.Item>
            </List>
          </Card.Section>
        )

      case 'inline':
        return (
          <Group position="apart" p="md" sx={{ borderBottom: `1px solid ${theme.colors.gray[3]}` }}>
            <Text>
              <strong>
                {validPoliciesCount > 1 ? 'Políticas' : 'Política'}{' '}
                {validPoliciesCount > 0 ? 'de' : ''} {policiesList}
              </strong>
            </Text>
            <Button variant="subtle" size="sm" onClick={() => setOpen(true)}>
              Ver políticas
            </Button>
          </Group>
        )

      default:
        return (
          <Alert
            title={`Políticas
              ${validPoliciesCount > 0 ? 'de' : ''} ${policiesList}`}>
            <Group position="right">
              <Button onClick={() => setOpen(true)}>Ver políticas</Button>
            </Group>
          </Alert>
        )
    }
  }

  return (
    <>
      <Box hidden={!Array.isArray(policies) || policies?.length === 0}>
        {isLoading ? renderSkeleton() : renderContent()}
      </Box>

      <Drawer
        opened={open}
        padding="lg"
        size={isXs ? 'full' : 'xl'}
        position="right"
        onClose={() => setOpen(false)}
        title={
          <Title order={3}>
            Políticas {validPoliciesCount > 0 ? 'de' : ''} {policiesList}
          </Title>
        }>
        <ScrollArea style={{ height: 'calc(100vh - 100px)' }} offsetScrollbars>
          {isLoading ? (
            renderDrawerSkeleton()
          ) : policiesWithContent?.length > 0 ? (
            <List
              listStyleType="none"
              classNames={{ item: classes.item, itemWrapper: classes.itemWrapper }}>
              {policiesWithContent.map((policy: PolicyType) => {
                const { uid: policyUid, title, content, dateUpdated } = policy
                const { html = '' } = content || {}

                return (
                  <List.Item key={policyUid}>
                    <Stack spacing="sm" py="md">
                      <Title order={4} mb={0}>
                        {title}
                      </Title>
                      {dateUpdated && (
                        <Text size="xs" color="dimmed">
                          Atualizado em <DateField type="datetime" value={dateUpdated} />
                        </Text>
                      )}
                      <Text size="sm" color="dimmed" dangerouslySetInnerHTML={{ __html: html }} />
                    </Stack>
                  </List.Item>
                )
              })}
            </List>
          ) : (
            <Stack align="center" justify="center" p="xl">
              <Text color="dimmed" size="lg">
                Nenhuma política disponível
              </Text>
            </Stack>
          )}
        </ScrollArea>
      </Drawer>
    </>
  )
}
